import { Injectable } from '@angular/core';
import { ID } from '@datorama/akita';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import intersect from '@turf/intersect';
import { Feature } from 'ol';
import { default as FillPattern } from 'ol-ext/style/FillPattern';
import { GeoJSON } from 'ol/format';
import { LineString, Polygon } from 'ol/geom';
import { default as Select, SelectEvent } from 'ol/interaction/Select';
import VectorLayer from 'ol/layer/Vector';
import VectorSource from 'ol/source/Vector';
import { Fill, Stroke, Style, Text } from 'ol/style';
import { StyleLike } from 'ol/style/Style';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';
import { filter, bufferTime, mergeMap } from 'rxjs/operators';
import { v4 as uuidv4 } from 'uuid';

import { CrowdFlowUtilsService } from '../../crowdflow/util/crowd-flow-utils.service';
import { roiStyleFunction } from '../../crowdflow/util/crowdflow-map-helper';
import { CrowdFlowAlertsQuery } from '../../crowdflow/state/crowd-flow-alerts.query';
import { CrowdFlowROIQuery } from '../../crowdflow/state/crowd-flow-roi.query';
import { CrowdFlowAlert } from '../../model/crowd-flow-alert';
import { CrowdFlowObserved } from '../../model/crowd-flow-observed';
import { CrowdFlowROI } from '../../model/crowd-flow-roi';
import { UrlConfig } from '../../model/urlconfig';


// ===================== STYLES ======================
const edgeDefaultStyle = new Style({
  stroke: new Stroke({ color: [88, 87, 255, 1], width: 7 }),
  zIndex: 50
});

export const edgeSelectedStyle: StyleLike = new Style({
  stroke: new Stroke({ color: [61, 255, 246, 1], width: 7 }),
  zIndex: 100
});

const sensorSiteStyle = new Style({
  fill: new Fill({ color: [250, 210, 25, 0.5] }),
});

const blindSpotStyle = new Style({
  stroke: new Stroke({
    color: [0, 0, 0, 1],
    width: 1
  }),
  fill: new FillPattern({
    color: [0, 0, 0, 0.75],
    pattern: 'hatch',
    size: 2,
    spacing: 5
  })
});

// Internal styles
const roiDefaultFill = new Fill({ color: [75, 168, 255, 0.75] });
const roiSelectedFill = new Fill({ color: [61, 255, 246, 0.75] });
const roiLabelText = new Text({
  font: '13px Roboto, "Helvetica Neue", sans-serif',
  fill: new Fill({ color: [255, 255, 255, 0.9] }),
  stroke: new Stroke({ color: [0, 0, 0, 0.8], width: 4 }),
  text: ''
});

/**
 * Extracts edges from an ROI polygon. Will set edgeIds if provided.
 */
export const extractEdgesFromRoi = (roiFeature: Feature, edgeIds = []): Feature[] => {
  const roiPolygon = roiFeature.getGeometry() as Polygon;
  const coordinates = roiPolygon.getLinearRing(0).getCoordinates(); // Get coordinates of the exterior ring

  const edges: Feature[] = [];
  for (let i = 0; i < coordinates.length - 1; i++) {
    const edge = new Feature(new LineString([coordinates[i], coordinates[i + 1]]));
    edge.setId(edgeIds[i] == null ? 'urn:ngsi-ld:CrowdFlowObserved:' + uuidv4() : edgeIds[i]);
    edge.setStyle(edgeDefaultStyle);
    edges.push(edge);
  }

  return edges;
};

// Feature flag keys
export const CROWD_FLOW_SENSOR_SITE = 'crowdflowSensortSite';
export const CROWD_COUNT_ID_KEY = 'crowdCountObservedId';
export const ROI_EDGE_IDS = 'roiEdgeIds';
export const SENSOR_SITE_BLIND_SPOT = 'isBlindSpot';
const USE_SELECTED_ROI_STYLE = 'useSelectedRoiStyle';
const CURRENT_PEOPLE_COUNT = 'currentPeopleCount';


@UntilDestroy()
@Injectable({ providedIn: 'root' })
export class CrowdFlowMapService implements UrlConfig {

  rois: Observable<CrowdFlowROI[]>;
  crowdFlows: Observable<CrowdFlowObserved[]>;
  crowdFlowAlerts: Observable<CrowdFlowAlert[]>;

  // The following Subjects represent the SSOT for the selection state for both the map and the CF component
  selectedRoiId = new BehaviorSubject<ID>(null);
  selectedEdgeId = new BehaviorSubject<ID>(null);
  selectedCrowdCountId = new BehaviorSubject<ID>(null);

  trackedCrowdCounts: { [key: string]: Subscription } = {};

  roiEdgeSelect: Select;
  areaSelect: Select;

  crowdFlowLayer: VectorLayer<VectorSource>;
  sensorSiteLayer: VectorLayer<VectorSource>;
  blindSpotLayer: VectorLayer<VectorSource>;

  constructor(
    private crowdFlowAlertsQuery: CrowdFlowAlertsQuery,
    private crowdFlowUtilsService: CrowdFlowUtilsService,
    private crowdlowROIQuery: CrowdFlowROIQuery,
  ) {
    this.rois = this.crowdlowROIQuery.selectAll();
    this.crowdFlows = this.crowdFlowUtilsService.selectAllCrowdFlows();
    this.crowdFlowAlerts = this.crowdFlowAlertsQuery.selectAll();
  }
  updateEntitiesUrl(fiwareUrl: string) {
    this.crowdFlowUtilsService.updateEntitiesUrl(fiwareUrl);
  }
  /**
   * OpenLayers StyleFunction for ROI feature objects. Sets both default
   * and selected styles based on an inernal flag.
   */
  private roiStyleFunction(roiFeature: Feature): Style {
    return new Style({
      fill: roiFeature.get(USE_SELECTED_ROI_STYLE) ? roiSelectedFill : roiDefaultFill,
      text: roiLabelText.clone()  // must be a clone, otherwise all ROIs will have the same label
    });
  }

  get getROIStyleFunction() {
    return this.roiStyleFunction;
  }

  edgeSelectEvent(event: SelectEvent) {
    const edgeId = event.selected[0]?.getId();
    this.selectedEdgeId.next(edgeId);
    this.updateRoiStyles(event);

    if (edgeId != null) {
      this.selectRoi(
        this.crowdFlowUtilsService.getAllCrowdFlowsAsObjects().find(roi => roi.edgeIds.includes(edgeId))?.id
      );
      this.selectRoiArea(null);
    }

    // Re-apply last selection if nothing is selected for the currently opened ROI.
    if (this.selectedRoiId.getValue() != null && edgeId == null && this.selectedCrowdCountId.getValue() == null) {
      this.selectRoiEdge(event.deselected[0].getId());
    }
    return false; // stop propagation
  }

  areaSelectEvent(event: SelectEvent) {
    const selectedRoiFeature = event.selected[0];

    // This event also triggers when an edge is seleced on the map, so we need to filter this out.
    if (selectedRoiFeature != null && !this.isRoiFeature(selectedRoiFeature)) {
      // in case we have an edge at hand we need to apply the edge style again
      selectedRoiFeature.setStyle(edgeSelectedStyle);
      return;
    }
    this.selectedCrowdCountId.next(selectedRoiFeature == null ? null : selectedRoiFeature.get(CROWD_COUNT_ID_KEY));
    this.updateRoiStyles(event);

    const selectedRoiId = selectedRoiFeature?.getId();
    if (selectedRoiId != null) {
      this.selectRoi(selectedRoiId);
      this.selectRoiEdge(null);
    }

    // Re-apply last selection if nothing is selected for the currently opened ROI.
    if (this.selectedRoiId.getValue() != null && selectedRoiFeature == null && this.selectedEdgeId.getValue() == null) {
      this.selectRoiArea(event.deselected[0].getId());
    }
    return false;
  }

  selectRoi(roiId: ID) {
    if (roiId === this.selectedRoiId.getValue()) { return; }
    this.selectedRoiId.next(roiId);
  }

  selectRoiEdge(edgeId: ID) {
    const currentlySelectedEdges = this.roiEdgeSelect.getFeatures();
    let event;

    if (edgeId == null) {
      event = {
        type: 'select',
        selected: [],
        deselected: [...currentlySelectedEdges.getArray()]
      };
      currentlySelectedEdges.clear();
    }
    else {
      const selectedEdge = this.crowdFlowLayer.getSource().getFeatureById(edgeId);
      event = {
        type: 'select',
        selected: [selectedEdge],
        deselected: [...currentlySelectedEdges.getArray()]
      };
      currentlySelectedEdges.clear();
      currentlySelectedEdges.push(selectedEdge);
    }

    this.roiEdgeSelect.dispatchEvent(event as SelectEvent);
  }

  selectRoiArea(roiId: ID) {
    const currentlySelectedAreas = this.areaSelect.getFeatures();
    let event;

    if (roiId == null) {
      event = {
        type: 'select',
        selected: [],
        deselected: [...currentlySelectedAreas.getArray()]
      };
      currentlySelectedAreas.clear();
    }
    else {
      const selectedArea = this.crowdFlowLayer.getSource().getFeatureById(roiId);
      if (selectedArea == null) {
        return;
      }
      event = {
        type: 'select',
        selected: [selectedArea],
        deselected: [...currentlySelectedAreas.getArray()]
      };
      currentlySelectedAreas.clear();
      currentlySelectedAreas.push(selectedArea);
      this.selectedEdgeId.next(null);

    }

    this.areaSelect.dispatchEvent(event as SelectEvent);
  }

  isRoiFeature(feature: Feature): boolean {
    return feature && ('' + feature.getId()).includes('CrowdFlowROI');
  }

  /**
   * Sets styles for all ROIs to selected/default based on a SelectEvent.
   */
  updateRoiStyles(selectEvent: SelectEvent) {
    selectEvent.deselected.forEach(feature => {
      feature.set(USE_SELECTED_ROI_STYLE, false);
      this.updateRoiStyleOrText(feature);
    });
    selectEvent.selected.forEach(feature => {
      feature.set(USE_SELECTED_ROI_STYLE, true);
      this.updateRoiStyleOrText(feature);
    });
  }

  updateRoiText(roiFeature: Feature, peopleCount: number) {
    roiFeature.set(CURRENT_PEOPLE_COUNT, peopleCount);
    this.updateRoiStyleOrText(roiFeature);
  }

  updateRoiStyleOrText(roiFeature: Feature) {
    if (!this.isRoiFeature(roiFeature)) { return; }

    const newStyle = roiStyleFunction(roiFeature);
    const amount = roiFeature.get(CURRENT_PEOPLE_COUNT) ?? 'N/A';
    newStyle.getText().setText(`\uD83D\uDC64 ${amount}`);
    roiFeature.setStyle(newStyle);
  }

  loadAll() {
    this.crowdFlowUtilsService.loadAllCrowdFlowData();
  }

  setAreaSelectAndEdgeSelect(areaSelect: Select, edgeSelect: Select) {
    this.areaSelect = areaSelect;
    this.roiEdgeSelect = edgeSelect;
    this.areaSelect.on('select', (evt: SelectEvent) => this.areaSelectEvent(evt));
    this.roiEdgeSelect.on('select', (evt: SelectEvent) => this.edgeSelectEvent(evt));
  }

  /**
   * Get layer for all kind of crowdFlowAlerts
   * TODO: Needs styling for features
   */
  createCrowdFlowAlertLayer() {
    const crowdFlowAlertLayerSource = new VectorSource();

    const crowdFlowAlertLayer = new VectorLayer({
      source: crowdFlowAlertLayerSource // TODO: style
    });

    this.crowdFlowAlerts.pipe(
      untilDestroyed(this),
      filter(data => data.length > 0)
    ).subscribe(data => data.map(crowdFlow => {
      const feature = new GeoJSON().readFeature(crowdFlow.area, { featureProjection: 'EPSG:3857' });
      crowdFlowAlertLayerSource.addFeature(feature);
    }));

    return crowdFlowAlertLayer;
  }

  createCrowdFlowLayer() {
    const crowdFlowLayerSource = new VectorSource();
    this.crowdFlowLayer = new VectorLayer({
      source: crowdFlowLayerSource,
      zIndex: 100,
      style: this.getROIStyleFunction
    });

    // Fetch ROIs from server
    const geo = new GeoJSON();
    this.rois.pipe(
      bufferTime(250),
      mergeMap(crowdFlows => crowdFlows),
      untilDestroyed(this),
      filter(data => data.length > 0)
    ).subscribe(rois => rois.forEach(roi => {
      const areaFeature = geo.readFeature(roi.area, { featureProjection: 'EPSG:3857' });
      const edgeFeatures = extractEdgesFromRoi(areaFeature, roi.edgeIds);
      areaFeature.setId(roi.id);
      areaFeature.set(CROWD_COUNT_ID_KEY, roi.crowdCountObservedId);
      areaFeature.set(ROI_EDGE_IDS, roi.edgeIds);
      crowdFlowLayerSource.addFeature(areaFeature);
      crowdFlowLayerSource.addFeatures(edgeFeatures);
    }));

    this.crowdFlows
      .pipe(
        bufferTime(250),
        mergeMap(crowdFlows => crowdFlows),
        untilDestroyed(this))
      .subscribe(data => data.map(crowdFlow => {
        const feature = new GeoJSON().readFeature(crowdFlow.location, { featureProjection: 'EPSG:3857' });
        crowdFlowLayerSource.addFeature(feature);
      }));

    // Every time an ROI is added to the map (by user or from server), start tracking its total people count
    // and keep the value displayed inside its polygon.
    crowdFlowLayerSource.on('addfeature', evt => {
      if (!this.isRoiFeature(evt.feature)) { return; }

      const crowdCountId = evt.feature.get(CROWD_COUNT_ID_KEY);
      if (!crowdCountId) { return; }

      if (!this.trackedCrowdCounts[crowdCountId]) {
        this.trackedCrowdCounts[crowdCountId] = this.crowdFlowUtilsService.selectCrowdCountById(crowdCountId)
          .pipe(untilDestroyed(this))
          .subscribe(crowdCount => {
            this.updateRoiText(evt.feature, crowdCount?.peopleCount);
          });
      }

    });

    return this.crowdFlowLayer;
  }

  createSensorSiteAndBlindspotLayers() {
    const sensorSiteLayerSource = new VectorSource();
    this.sensorSiteLayer = new VectorLayer({
      source: sensorSiteLayerSource,
      zIndex: 50,
      style: sensorSiteStyle
    });

    const blindSpotLayerSource = new VectorSource();
    this.blindSpotLayer = new VectorLayer({
      source: blindSpotLayerSource,
      zIndex: 150,
      style: blindSpotStyle
    });

    const geo = new GeoJSON();
    this.crowdFlowUtilsService.selectAllCrowdFlowSensorSites()
      .pipe(
        bufferTime(250),
        mergeMap(crowdFlows => crowdFlows),
        untilDestroyed(this))
      .subscribe(
        data => data.map(sensorSite => {
          const fieldOfView = geo.readFeature(sensorSite.fieldOfView, { featureProjection: 'EPSG:3857' });
          fieldOfView.setId(sensorSite.id);
          sensorSiteLayerSource.addFeature(fieldOfView);

          const blindspots = geo.readFeature(sensorSite.blindspots, { featureProjection: 'EPSG:3857' });
          blindspots.set(CROWD_FLOW_SENSOR_SITE, sensorSite.id);
          blindspots.set(SENSOR_SITE_BLIND_SPOT, true);  // Only necessary if blind spots are defined as separate features
          blindSpotLayerSource.addFeature(blindspots);
        }),
        error => console.log('Could not establish sensor site subscription', error)
      );
    return { sensorSiteLayer: this.sensorSiteLayer, blindSpotLayer: this.blindSpotLayer };
  }

  getAllCrowdFlowsAsObjects() {
    return this.crowdFlowUtilsService.getAllCrowdFlowsAsObjects();
  }

  insertRoi(roiFeature: Feature): CrowdFlowROI {
    const geo = new GeoJSON();
    const roiGeoJson = geo.writeFeatureObject(roiFeature) as any;
    const roiGeometry = roiFeature.getGeometry() as Polygon;

    const roiId = roiFeature.getId();
    roiFeature.setId('urn:ngsi-ld:CrowdFlowROI:' + (roiId == null ? uuidv4() : roiId));

    const crowdCountId = 'urn:ngsi-ld:CrowdCountObserved:' + uuidv4();
    roiFeature.set(CROWD_COUNT_ID_KEY, crowdCountId);

    // Determine used sensor sites.
    const intersectingSensorSiteFovs = this.sensorSiteLayer.getSource().getFeatures()
      .filter(fovPolygon => !fovPolygon.get(SENSOR_SITE_BLIND_SPOT))  // ignore blindspots
      .filter(sensorSite => {
        let intersection;
        try {
          intersection = intersect(roiGeoJson, geo.writeFeatureObject(sensorSite) as any);
        } catch (ignore) {
          // Ignore SensorSites that throw errors on intersection. Can happen due to, e.g., invalid geometry.
        }
        return intersection != null;
      })
      .sort((siteA, siteB) => {
        // Sort intersecting SensorSites by distance from the first vertex of ROI
        // The following distance calculation is only suitable for short distances as it ignores earth's curvature.
        const referencePoint = roiGeometry.getFirstCoordinate();
        const closestA = siteA.getGeometry().getClosestPoint(referencePoint);
        const closestB = siteB.getGeometry().getClosestPoint(referencePoint);
        return new LineString([referencePoint, closestA]).getLength() - new LineString([referencePoint, closestB]).getLength();
      });

    // If the ROI is not within any SensorSite, abort.
    if (intersectingSensorSiteFovs.length === 0) { return null; }

    // Clip ROI to the closest SensorSite (first in the array).
    const closestSensorSiteFov = intersectingSensorSiteFovs[0];
    const clippedRoiFeature = geo.readFeature(intersect(roiGeoJson, geo.writeFeatureObject(closestSensorSiteFov) as any));
    roiFeature.setGeometry(clippedRoiFeature.getGeometry());

    // Extract edges and add them to the layer.
    const roiEdges = extractEdgesFromRoi(roiFeature);
    roiFeature.set(ROI_EDGE_IDS, roiEdges.map(edge => edge.getId()));
    this.crowdFlowLayer.getSource().addFeatures(roiEdges);

    // Create a new ROI object and save it.
    const roi: CrowdFlowROI = {
      type: 'CrowdFlowROI',
      id: roiFeature.getId(),
      sensorSiteId: closestSensorSiteFov.getId(),
      area: geo.writeGeometryObject(roiFeature.getGeometry(), { featureProjection: 'EPSG:3857' }),
      active: true,
      edgeIds: roiEdges.map(edge => edge.getId()),
      crowdCountObservedId: crowdCountId,
    };
    this.crowdFlowUtilsService.insertROI(roi);

    return roi;
  }

  deleteRoi(roi: CrowdFlowROI) {

    const roiLayerSource = this.crowdFlowLayer.getSource();
    const associatedEdgeIds = [...roi.edgeIds];   // This needs to be a copy.

    roiLayerSource.removeFeature(roiLayerSource.getFeatureById(roi.id));
    this.crowdFlowUtilsService.deleteRoi(roi);

    // Delete all associated edges
    for (const edgeId of associatedEdgeIds) {
      const edge = roiLayerSource.getFeatureById(edgeId);
      if (edge) { roiLayerSource.removeFeature(edge); }
    }

    // Remove associated CrowdCount tracker.
    this.trackedCrowdCounts[roi.crowdCountObservedId]?.unsubscribe();
    delete this.trackedCrowdCounts[roi.crowdCountObservedId];
  }
}
